/**
 * The purchase planner's phone half (mobile M3).
 *
 * The desktop planner draws eight weeks of lanes with a draggable cursor — a
 * picture that needs ~900px. On a phone the same two questions get shapes that
 * fit a thumb:
 *
 *   * **DayScrubber** — "which day?": a strip of days you swipe through and
 *     tap. Each day is underlined in the colour of the card that would give the
 *     longest float that day (the desktop's ribbon), so good windows still show
 *     without trying every day.
 *   * **CycleTimeline** — "what happens when?": the coming closes and bills
 *     as a vertical timeline. A printed due date is gold and solid; a projected
 *     one is outlined, dashed and says "est." (§2.7).
 */

import * as React from "react";
import { Link } from "react-router-dom";

import { addDays, cardName, daysBetween, floatsOn } from "@/components/cycles/cycleMath";
import { CardSwatch, Panel, ProvisionalChip } from "@/components/ui/primitives";
import type { CycleCard } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { dayParts, formatDate } from "@/lib/format";
import { haptic } from "@/platform";

const WEEKDAY = ["S", "M", "T", "W", "T", "F", "S"];

export function DayScrubber({
  cards,
  today,
  last,
  value,
  onChange,
}: {
  cards: CycleCard[];
  today: string;
  last: string;
  value: string;
  onChange: (date: string) => void;
}) {
  const days = React.useMemo(() => {
    const out: { date: string; best: CycleCard | null; days: number | null }[] = [];
    for (let i = 0; i <= daysBetween(today, last); i++) {
      const d = addDays(today, i);
      const top = floatsOn(cards, d)[0];
      out.push({ date: d, best: top?.card ?? null, days: top?.days ?? null });
    }
    return out;
  }, [cards, today, last]);

  const strip = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = strip.current?.querySelector<HTMLElement>(`[data-date="${value}"]`);
    el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [value]);

  return (
    <div
      ref={strip}
      role="listbox"
      aria-label="Purchase date"
      className="-mx-4 flex snap-x gap-1.5 overflow-x-auto px-4 pb-2 [scrollbar-width:none]"
    >
      {days.map(({ date, best, days: float }) => {
        const p = dayParts(date);
        const on = date === value;
        const monthStart = p.day === 1 || date === today;
        return (
          <button
            key={date}
            data-date={date}
            type="button"
            role="option"
            aria-selected={on}
            aria-label={`${formatDate(date)}${best ? `: ${cardName(best)}, ${float} days to pay` : ""}`}
            onClick={() => {
              haptic("tick");
              onChange(date);
            }}
            className={cn(
              "relative flex h-[4.25rem] w-12 shrink-0 snap-center flex-col items-center justify-center gap-0.5 rounded-xl border transition-colors",
              on ? "border-brass/60 bg-brass/[0.12]" : "border-hairline bg-panel",
            )}
          >
            <span className="text-[10px] text-faint">{monthStart ? p.month : WEEKDAY[p.weekday]}</span>
            <span className={cn("text-[15px] tabular-nums", on ? "text-ink" : "text-muted")}>{p.day}</span>
            <span
              aria-hidden
              className="absolute inset-x-2 bottom-1.5 h-[3px] rounded-full bg-hairline-strong"
              style={
                best?.theme
                  ? { backgroundImage: `linear-gradient(90deg, ${best.theme.gradient_from}, ${best.theme.gradient_to})` }
                  : undefined
              }
            />
          </button>
        );
      })}
    </div>
  );
}

type TimelineEvent = {
  date: string;
  card: CycleCard;
  kind: "close" | "due";
  printed: boolean;
};

/** Closes and bills from today to `until`, soonest first. */
export function timelineEvents(cards: CycleCard[], today: string, until: string): TimelineEvent[] {
  const out: TimelineEvent[] = [];
  for (const card of cards) {
    if (card.cycle_end && card.cycle_end >= today && card.cycle_end <= until) {
      out.push({ date: card.cycle_end, card, kind: "close", printed: false });
    }
    // A printed due date wins; a projection is shown only when nothing is printed.
    const printedDue = card.outstanding_due_date;
    if (printedDue && printedDue >= today && printedDue <= until && card.outstanding_status !== "paid") {
      out.push({ date: printedDue, card, kind: "due", printed: true });
    } else if (card.due_expected_on && card.due_expected_on >= today && card.due_expected_on <= until) {
      out.push({ date: card.due_expected_on, card, kind: "due", printed: false });
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind));
}

export function CycleTimeline({ cards, today, until }: { cards: CycleCard[]; today: string; until: string }) {
  const events = timelineEvents(cards, today, until);
  if (events.length === 0) return null;
  return (
    <Panel className="p-4">
      <h3 className="label-micro mb-3">Coming up</h3>
      <ol className="relative space-y-0 border-l border-hairline pl-4">
        {events.map((e, i) => (
          <li key={`${e.card.card_id}-${e.kind}-${i}`} className="relative py-2">
            <span
              aria-hidden
              className={cn(
                "absolute -left-[21px] top-1/2 h-2.5 w-2.5 -translate-y-1/2",
                e.kind === "due"
                  ? e.printed
                    ? "rotate-45 bg-brass"
                    : "rotate-45 border border-dashed border-ink/60 bg-base"
                  : "rounded-full border border-hairline-strong bg-panel",
              )}
            />
            <Link to={`/cards/${e.card.card_id}`} className="flex items-center gap-3">
              <span className="w-12 shrink-0 text-xs tabular-nums text-faint">
                {e.date === today ? "Today" : `${dayParts(e.date).day} ${dayParts(e.date).month}`}
              </span>
              <CardSwatch theme={e.card.theme} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-ink">{cardName(e.card)}</span>
                <span className="flex items-center gap-1.5 text-xs text-muted">
                  {e.kind === "close" ? "Cycle closes" : "Bill due"}
                  {!e.printed && <ProvisionalChip kind="est" />}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </Panel>
  );
}
